import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import emailjs from "emailjs-com";
import { BASE_URL, EMAIL_SERVICE_ID, EMAIL_TEMPLATE_ID, EMAIL_PUBLIC_KEY } from "../../store/config";
import ErrorModal from "../../components/common/ErrorModal";

function LoginOtp() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [apiError, setApiError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const navigate = useNavigate();

  // ✅ Only navigate in useEffect (prevents double navigation in tests)
  useEffect(() => {
    if (isAuthenticated) navigate("/welcome");
  }, [isAuthenticated, navigate]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();

  const hashOtp = async (otp) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(otp);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const sendOtpEmail = async (otpCode) => {
    return emailjs.send(
      EMAIL_SERVICE_ID,
      EMAIL_TEMPLATE_ID,
      { email, passcode: otpCode },
      EMAIL_PUBLIC_KEY
    );
  };

  const sendOtp = async () => {
    if (!email.trim()) return showError("Email is required");
    if (!isValidEmail(email)) return showError("Enter a valid email");

    try {
      const otpGenerated = generateOtp();
      const hashedOtp = await hashOtp(otpGenerated);

      sessionStorage.setItem("otpHash", hashedOtp);
      sessionStorage.setItem("otpExpiry", Date.now() + 2 * 60 * 1000);

      await sendOtpEmail(otpGenerated);
      setOtpSent(true);
    } catch {
      showError("Failed to send OTP. Try again.");
    }
  };

  const showError = (message) => {
    setApiError(message);
    setShowErrorModal(true);
  };

  const loginWithOtpApi = async () => {
    const res = await fetch(`${BASE_URL}api/auth/login_otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "OTP login failed");
    return data;
  };

  const verifyOtp = async () => {
    if (!otp) return showError("OTP is required");
    if (!/^\d{6}$/.test(otp)) return showError("OTP must be 6 digits");

    const storedHash = sessionStorage.getItem("otpHash");
    const expiry = Number(sessionStorage.getItem("otpExpiry"));
    if (!storedHash || Date.now() > expiry) return showError("OTP expired. Please resend.");

    const enteredHash = await hashOtp(otp);
    if (enteredHash !== storedHash) return showError("Invalid OTP");

    try {
      sessionStorage.removeItem("otpHash");
      sessionStorage.removeItem("otpExpiry");

      const data = await loginWithOtpApi();

      localStorage.setItem("access_token", data.access_token);
      sessionStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("first_time_user", data.is_first_user ? "True" : "False");

      setIsAuthenticated(true);
    } catch (err) {
      showError(err.message || "OTP login failed");
    }
  };

  // ✅ Only redirect in actual browser environment
  if (isAuthenticated && typeof window !== "undefined") return <Navigate to="/welcome" replace />;

  return (
    <div>
      <div className="input-group">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {otpSent && (
        <div className="input-group">
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={6}
          />
        </div>
      )}

      {!otpSent ? (
        <button className="btn" onClick={sendOtp}>
          Send OTP
        </button>
      ) : (
        <button className="btn" onClick={verifyOtp}>
          Verify OTP
        </button>
      )}

      <ErrorModal
        show={showErrorModal}
        message={apiError}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
}

export default LoginOtp;